import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Query param shortcuts read from the host page URL.
 * The `sb-` prefix avoids collisions with params the customer page may
 * already use for its own purposes.
 *
 * SB-7560:
 *  - `sb-session-id` / `sb-speaker-id` — open the matching modal (existing)
 *  - `sb-session-ids` — comma-separated session ids to highlight in the list
 */
export const URL_RECORD_PARAMS = {
  SESSION: 'sb-session-id',
  SPEAKER: 'sb-speaker-id',
  SESSION_IDS: 'sb-session-ids',
};

const readUrlParam = key => {
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    // Very old browsers or exotic host environments: shortcuts just no-op.
    return null;
  }
};

/**
 * Parse `sb-session-ids` (and fall back to a lone `sb-session-id`) into a
 * de-duped list of trimmed ids for list highlighting.
 */
export const readHighlightedSessionIds = () => {
  const multi = readUrlParam(URL_RECORD_PARAMS.SESSION_IDS);
  const single = readUrlParam(URL_RECORD_PARAMS.SESSION);
  const raw = multi || (single ? single : '');
  if (!raw) return [];
  const seen = new Set();
  const out = [];
  for (const part of raw.split(',')) {
    const id = part.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

/**
 * Auto-opens a record modal when the host page URL contains a shortcut
 * query param (e.g. ?sb-session-id=<id>) and that record exists in the
 * embed's loaded data. Unknown or malformed ids are ignored silently.
 *
 * @param {Object} options
 * @param {boolean} options.ready True when data has loaded and `exists` can
 *   resolve ids (include the widget's useModals flag here so deep links
 *   respect the embed's modal setting).
 * @param {Array<{param: string, modalType: string, exists: (id: string) => boolean}>} options.shortcuts
 *   Shortcut definitions in priority order.
 * @param {(type: string, id: string) => void} options.openModal The widget's
 *   openModal from useModalStack.
 * @returns {void}
 */
export const useOpenRecordFromUrl = ({ ready, shortcuts, openModal }) => {
  const handledRef = useRef(false);

  // Callers pass inline literals; track them via refs so the effect can
  // depend on `ready` alone instead of re-subscribing on every render.
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;
  const openModalRef = useRef(openModal);
  openModalRef.current = openModal;

  useEffect(() => {
    if (!ready || handledRef.current) return;

    let sawParam = false;
    for (const { param, modalType, exists } of shortcutsRef.current) {
      const rawId = readUrlParam(param);
      if (!rawId) continue;
      sawParam = true;

      const id = rawId.trim();
      const matchingId = exists(id) ? id : exists(id.toLowerCase()) ? id.toLowerCase() : null;

      if (matchingId) {
        handledRef.current = true;
        openModalRef.current(modalType, matchingId);
        return;
      }
    }

    // Lock out only once `ready` means the dataset is complete (caller
    // responsibility). If a param was present but not found, don't retry.
    // If no deep-link params, we're done immediately.
    if (!sawParam || ready) {
      handledRef.current = true;
    }
  }, [ready]);
};

/**
 * Returns a Set of session ids to highlight from the host URL. Re-reads when
 * `ready` flips true so late-loaded widgets still pick up the deep link.
 */
export const useHighlightedSessionIds = ({ ready = true } = {}) => {
  const [ids, setIds] = useState([]);

  useEffect(() => {
    if (!ready) return;
    setIds(readHighlightedSessionIds());
  }, [ready]);

  return useMemo(() => new Set(ids), [ids]);
};
